import React, { useState, useEffect, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import LoginScreen from './LoginScreen';
import ConfigurationScreen from './ConfigurationScreen';
import VocabularyScreen, { Vocable as VocabularyListVocable } from './VocabularyScreen';
import ViewVocableScreen from './ViewVocableScreen';
import AddEditVocableScreen, { AddEditVocableFormValues } from './AddEditVocableScreen';
import ImprintScreen from './ImprintScreen';
import { authService } from '../services/authService';
import { configService, UserConfiguration, LanguageMode } from '../services/configService';
import { getLearningMethodShortLabel, getOutputModeShortLabel, getLanguageModeShortLabel } from '../utils/configLabels';
import { VERB_FORMS } from '../utils/verbFormLabels';
import { buildAnswerWithinWidth, isAnswerCorrect as evaluateAnswerCorrect, PromptMode } from '../utils/trainingAnswerUtils';
import { authenticatedFetch } from '../utils/apiUtils';
import './App.css';

interface TrainingVocable {
  id: string;
  japanese: string;
  kana: string;
  english: string[];
  conjugations?: Record<string, string>;
  kanjiMapping?: Record<string, string>;
  username?: string;
  nonDesiredVocables?: string | null;
  focusIndex?: number;
}

interface TrainingPrompt {
  vocable: TrainingVocable;
  promptMode: PromptMode;
  englishPrompt?: string;
}

type AppState = 'login' | 'start' | 'training' | 'configure';

function App() {
  const [appState, setAppState] = useState<AppState>('login');
  const [state, setState] = useState<'start' | 'training'>('start');
  const [trainingPrompts, setTrainingPrompts] = useState<TrainingPrompt[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentCorrectIndex, setCurrentCorrectIndex] = useState(0);
  const [currentPrompt, setCurrentPrompt] = useState<TrainingPrompt | null>(null);
  const [userInput, setUserInput] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [gradeButtonsState, setGradeButtonsState] = useState<'hidden' | 'correct' | 'incorrect'>('hidden');
  const [typoUsed, setTypoUsed] = useState(false);
  const [wasAnswerEmpty, setWasAnswerEmpty] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [flashcardCorrectSelected, setFlashcardCorrectSelected] = useState(false);
  const [isTrainingLoading, setIsTrainingLoading] = useState(false);
  const [trainingError, setTrainingError] = useState('');
  const [focusStatus, setFocusStatus] = useState<'focused' | 'loading' | 'base-focus' | null>(null);
  const [retryVocableIds, setRetryVocableIds] = useState<Set<string>>(new Set());
  const previousVocableIdRef = useRef<string | null>(null);
  const trainingPromptsRef = useRef<TrainingPrompt[]>([]);
  const [reviewResults, setReviewResults] = useState<{ id: string; grade: number }[]>([]);
  const reviewResultsRef = useRef<{ id: string; grade: number }[]>([]);
  const correctAnswerRef = useRef<HTMLDivElement | null>(null);
  const [answerDisplayWidth, setAnswerDisplayWidth] = useState<number | null>(null);
  const [answerDisplayFont, setAnswerDisplayFont] = useState<string>(
    '600 14px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  );

  const reduceScriptSwitchingFirstHalfRef = useRef<boolean | null>(null);
  const previousStateRef = useRef<'start' | 'training'>('start');
  const previousShowConfigurationRef = useRef(false);
  const previousShowVocabularyRef = useRef(false);

  const createTrainingPrompts = useCallback((
    vocables: TrainingVocable[],
    languageMode: LanguageMode | undefined,
    reduceScriptSwitching: boolean | undefined
  ): TrainingPrompt[] => {
    const mode = languageMode ?? 'MIXED';
    const reduceSwitching = reduceScriptSwitching ?? false;

    const pickEnglishPrompt = (vocable: TrainingVocable): string | undefined => {
      if (!vocable.english || vocable.english.length === 0) {
        return undefined;
      }
      const index = Math.floor(Math.random() * vocable.english.length);
      return vocable.english[index];
    };

    // If reduceScriptSwitching is enabled and mode is MIXED, split the vocables in half
    if (reduceSwitching && mode === 'MIXED') {
      const totalPrompts = vocables.length;
      const halfPoint = Math.ceil(totalPrompts / 2);
      
      // Randomly choose which language comes first
      const firstLanguageIsEnglish = Math.random() < 0.5;
      reduceScriptSwitchingFirstHalfRef.current = firstLanguageIsEnglish;

      return vocables.map((vocable, index) => {
        let promptMode: PromptMode = 'JAPANESE';
        let englishPrompt: string | undefined;

        // Determine which language to use based on position
        const shouldUseEnglish = index < halfPoint 
          ? firstLanguageIsEnglish 
          : !firstLanguageIsEnglish;

        if (shouldUseEnglish) {
          const prompt = pickEnglishPrompt(vocable);
          if (prompt) {
            promptMode = 'ENGLISH';
            englishPrompt = prompt;
          }
        }

        return {
          vocable,
          promptMode,
          englishPrompt,
        };
      });
    } else {
      reduceScriptSwitchingFirstHalfRef.current = null;
    }

    // Default behavior: random assignment for MIXED mode
    return vocables.map((vocable) => {
      let promptMode: PromptMode = 'JAPANESE';
      let englishPrompt: string | undefined;

      const shouldUseEnglish =
        mode === 'ENGLISH' ||
        mode === 'VERBS' ||
        (mode === 'MIXED' && Math.random() < 0.5);

      if (shouldUseEnglish) {
        const prompt = pickEnglishPrompt(vocable);
        if (prompt) {
          promptMode = 'ENGLISH';
          englishPrompt = prompt;
        }
      }

      return {
        vocable,
        promptMode,
        englishPrompt,
      };
    });
  }, []);
  
  // Check if configuration screen should be shown
  const [showConfiguration, setShowConfiguration] = useState(false);
  
  // Check if vocabulary screen should be shown
  const [showVocabulary, setShowVocabulary] = useState(false);
  const [showAddEditVocable, setShowAddEditVocable] = useState(false);
  const [showViewVocable, setShowViewVocable] = useState(false);
  const [vocableToView, setVocableToView] = useState<VocabularyListVocable | null>(null);
  const [vocableToEdit, setVocableToEdit] = useState<VocabularyListVocable | null>(null);
  const [vocabularyRefreshToken, setVocabularyRefreshToken] = useState(0);
  const [returnToViewAfterEdit, setReturnToViewAfterEdit] = useState(false);
  
  // Imprint screen
  const [showImprintScreen, setShowImprintScreen] = useState(false);
  
  // Store current configuration
  const [config, setConfig] = useState<UserConfiguration | null>(null);

  // Load configuration
  const loadConfiguration = useCallback(async () => {
    try {
      const loadedConfig = await configService.getConfiguration();
      setConfig({
        ...loadedConfig,
        languageMode: loadedConfig.languageMode ?? 'MIXED',
      });
    } catch (err) {
      console.error('Failed to load configuration:', err);
      // Set default config if load fails
      setConfig({
        vocableNumber: 10,
        learningMethod: 'SPACED_REPETITION',
        outputMode: 'DEFAULT',
        languageMode: 'MIXED',
      });
    }
  }, []);

  // Check authentication on mount
  useEffect(() => {
    if (authService.isAuthenticated()) {
      setAppState('start');
      loadConfiguration();
    } else {
      setAppState('login');
    }
  }, [loadConfiguration]);

  // Check token expiration once when appState changes (not on login screen)
  useEffect(() => {
    if (appState === 'login') {
      return; // Don't check if already on login screen
    }

    // Check token expiration once
    if (!authService.isAuthenticated()) {
      // Token expired or invalid, redirect to login
      authService.removeToken();
      setAppState('login');
    }
  }, [appState]);

  // Clear training error when navigating to start screen
  useEffect(() => {
    if (state === 'start' && previousStateRef.current !== 'start') {
      setTrainingError('');
    }
    previousStateRef.current = state;
  }, [state]);

  // Clear training error when navigating back from configure or vocabulary screens
  useEffect(() => {
    // Clear error when navigating back from configure screen
    if (previousShowConfigurationRef.current && !showConfiguration && state === 'start') {
      setTrainingError('');
    }
    // Clear error when navigating back from vocabulary screen
    if (previousShowVocabularyRef.current && !showVocabulary && state === 'start') {
      setTrainingError('');
    }
    previousShowConfigurationRef.current = showConfiguration;
    previousShowVocabularyRef.current = showVocabulary;
  }, [showConfiguration, showVocabulary, state]);

  const getConfigSummary = (): string => {
    if (!config) return 'Loading configuration...';
    
    const methodLabel = getLearningMethodShortLabel(config.learningMethod);
    const modeLabel = getOutputModeShortLabel(config.outputMode);
    const languageLabel = getLanguageModeShortLabel(config.languageMode);

    
    return `${config.vocableNumber} • ${methodLabel} • ${modeLabel} • ${languageLabel}`;
  };

  const handleLoginSuccess = () => {
    setAppState('start');
    setState('start');
    setShowConfiguration(false);
    setShowVocabulary(false);
    setShowAddEditVocable(false);
    setShowViewVocable(false);
    loadConfiguration();
  };

  const handleLogout = () => {
    authService.removeToken();
    setAppState('login');
    setShowVocabulary(false);
    setShowAddEditVocable(false);
    setVocableToEdit(null);
    setTrainingPrompts([]);
    setCurrentIndex(0);
    setCurrentPrompt(null);
    setTrainingError('');
    setSessionComplete(false);
    setAnsweredCount(0);
    setCorrectCount(0);
    setGradeButtonsState('hidden');
    setTypoUsed(false);
  };

  const handleStart = useCallback(async () => {
    setTrainingError('');
    setIsTrainingLoading(true);
    setSessionComplete(false);
    setAnsweredCount(0);
    setCorrectCount(0);
    setGradeButtonsState('hidden');
    setTypoUsed(false);
    setWasAnswerEmpty(false);
    setFlashcardCorrectSelected(false);
    setRetryVocableIds(new Set());
    setReviewResults([]);
    reviewResultsRef.current = [];

    try {
      const response = await authenticatedFetch('/api/training/vocables');

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to fetch training vocables');
      }

      const data: TrainingVocable[] = await response.json();

      if (!Array.isArray(data) || data.length === 0) {
        setTrainingPrompts([]);
        setCurrentPrompt(null);
        setTrainingError('No vocabulary available for training yet.');
        setState('start');
        return;
      }

      const prompts = createTrainingPrompts(data, config?.languageMode, config?.reduceScriptSwitching);

      if (prompts.length === 0) {
        setTrainingPrompts([]);
        setCurrentPrompt(null);
        setTrainingError('No vocabulary available for training yet.');
        setState('start');
        return;
      }

      setTrainingPrompts(prompts);
      initialPromptsCountRef.current = prompts.length;
      setCurrentCorrectIndex(0);
      setCurrentIndex(0);
      setCurrentPrompt(prompts[0]);
      setUserInput('');
      setShowAnswer(false);
      setIsCorrect(false);
      setState('training');
    } catch (error) {
      console.error('Failed to start training session', error);
      setTrainingError(
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while starting training.'
      );
      setTrainingPrompts([]);
      setCurrentPrompt(null);
      setState('start');
      setReviewResults([]);
      reviewResultsRef.current = [];
    } finally {
      setIsTrainingLoading(false);
    }
  }, [config?.languageMode, config?.reduceScriptSwitching, createTrainingPrompts]);

  const addVocableToPromptsAtRandom = (trainingPrompts: TrainingPrompt[], prompt: TrainingPrompt, currentPos: number): TrainingPrompt[] => {
    let currentPromptCopy: TrainingPrompt = {
      ...prompt,
    };

    const newPrompts = trainingPrompts; // Use prevPrompts instead of trainingPrompts
    // Calculate a random position after the current one, but within the remaining prompts
    const remainingPrompts = newPrompts.length - currentPos - 1;
    const randomOffset = remainingPrompts > 0 
      ? Math.floor(Math.random() * (remainingPrompts + 1))
      : 0;
    const insertPosition = currentPos + 1 + randomOffset;
    
    // If reduceScriptSwitching is enabled and languageMode is MIXED, determine which language to use
    if (config?.reduceScriptSwitching && config?.languageMode === 'MIXED' && trainingPrompts.length > 0) {
      const totalPrompts = initialPromptsCountRef.current || trainingPrompts.length;
      const halfPoint = Math.ceil(totalPrompts / 2);
      
      // Determine which language was used in the first half from the ref (set during prompt creation)
      const firstHalfLanguageIsEnglish = reduceScriptSwitchingFirstHalfRef.current ?? true;
      
      // Determine which half we're currently in based on currentCorrectIndex
      const isInFirstHalf = insertPosition < halfPoint;
      
      // Set the prompt mode based on which half we're in
      const shouldUseEnglish = isInFirstHalf 
        ? firstHalfLanguageIsEnglish 
        : !firstHalfLanguageIsEnglish;
      
      if (shouldUseEnglish) {
        // Pick an English prompt if available
        const englishPrompts = prompt.vocable.english;
        if (englishPrompts && englishPrompts.length > 0) {
          const index = Math.floor(Math.random() * englishPrompts.length);
          currentPromptCopy = {
            ...prompt,
            promptMode: 'ENGLISH',
            englishPrompt: englishPrompts[index],
          };
        } else {
          // Fallback to Japanese if no English available
          currentPromptCopy = {
            ...prompt,
            promptMode: 'JAPANESE',
            englishPrompt: undefined,
          };
        }
      } else {
        // Use Japanese
        currentPromptCopy = {
          ...prompt,
          promptMode: 'JAPANESE',
          englishPrompt: undefined,
        };
      }
    }
    
    let updatedPrompts: TrainingPrompt[] = [];

      
    newPrompts.splice(insertPosition, 0, currentPromptCopy);
    updatedPrompts = newPrompts;
    
    setTrainingPrompts((prevPrompts) => {
      trainingPromptsRef.current = newPrompts; // Update ref immediately
      return newPrompts; // Return the new state
    });
    
    return updatedPrompts; // Return the updated prompts for immediate use
  };

  const submitReviewResults = useCallback(async () => {
    const payload = reviewResultsRef.current;
    if (!payload.length) {
      return;
    }

    try {
      const response = await authenticatedFetch('/api/training/review', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to apply vocable reviews', errorText || response.statusText);
        return;
      }

      setReviewResults([]);
      reviewResultsRef.current = [];
    } catch (error) {
      console.error('Error applying vocable reviews', error);
    }
  }, []);

  const handleCheckAnswer = () => {
    if (!currentPrompt || showAnswer || sessionComplete) {
      return;
    }

    const isFlashcardMode = config?.outputMode === 'FLASHCARD';

    if (isFlashcardMode) {
      // In flashcard mode, just show the Wrong/Correct buttons without evaluating
      setShowAnswer(true);
      setFlashcardCorrectSelected(false);
      setGradeButtonsState('hidden');
      return;
    }

    // Default mode: evaluate the answer
    const vocable = currentPrompt.vocable;
    const vocableId = vocable.id;
    const isRetryVocable = retryVocableIds.has(vocableId);
    const isEmpty = !userInput.trim();
    const includedConjugations = config?.includedConjugations;
    const isAnswerCorrect = evaluateAnswerCorrect(currentPrompt.promptMode, userInput, vocable, includedConjugations);

    setIsCorrect(isAnswerCorrect);
    setShowAnswer(true);
    setWasAnswerEmpty(isEmpty);
    
    setGradeButtonsState(isAnswerCorrect ? 'correct' : 'incorrect');
    setTypoUsed(false);
  };

  const advanceToNextVocable = (updatedPrompts?: TrainingPrompt[]) => {
    const promptsToUse = updatedPrompts ?? trainingPromptsRef.current; // Use passed prompts or fallback to ref
    const nextIndex = currentIndex + 1;

    if (nextIndex >= promptsToUse.length) {
      setSessionComplete(true);
      setGradeButtonsState('hidden');
      setTypoUsed(false);
      setWasAnswerEmpty(false);
      setFlashcardCorrectSelected(false);
      setUserInput('');
      return;
    }

    setCurrentIndex(nextIndex);
    setCurrentPrompt(promptsToUse[nextIndex]); // Use promptsToUse instead of trainingPrompts
    setShowAnswer(false);
    setUserInput('');
    setIsCorrect(false);
    setGradeButtonsState('hidden');
    setTypoUsed(false);
    setWasAnswerEmpty(false);
    setFlashcardCorrectSelected(false);
  };

  const handleTypo = () => {
    if (!showAnswer || sessionComplete || gradeButtonsState !== 'incorrect' || typoUsed || !currentPrompt) {
      return;
    }

    const vocableId = currentPrompt.vocable.id;
    const isRetryVocable = retryVocableIds.has(vocableId);
    
    setGradeButtonsState('correct');
    setTypoUsed(true);
    setIsCorrect(true);
  };

  const handleGrade = (_grade: number) => {
    console.log('retryVocableIds', retryVocableIds);

    const isFlashcardMode = config?.outputMode === 'FLASHCARD';

    if (!showAnswer || sessionComplete || (gradeButtonsState === 'hidden' && !isFlashcardMode) || !currentPrompt) {
      return;
    }

    const vocableId = currentPrompt.vocable.id;
    var isRetryVocable = retryVocableIds.has(vocableId);
    var newPrompts = trainingPrompts;

    if(_grade >= 1) {
      setCurrentCorrectIndex((prev) => prev + 1);
    }

    // Only update counters and review results for non-retry vocables
    if (!isRetryVocable) {
      setAnsweredCount((prev) => prev + 1);
      if (_grade >= 1) {
        setCorrectCount((prev) => prev + 1);
      }

      setReviewResults((prev) => {
        const updated = [...prev, { id: vocableId, grade: _grade }];
        reviewResultsRef.current = updated;
        return updated;
      });
    } else {
      if(_grade >= 1) {
        setRetryVocableIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(vocableId);
          return newSet;
        });
      }

      // Remove all future occurrences of this vocable from the training prompts
        for (let i = newPrompts.length - 1; i > currentIndex; i--) {
          if (newPrompts[i].vocable.id === vocableId) {
            newPrompts.splice(i, 1);
          }
        }
        trainingPromptsRef.current = newPrompts; // Update ref immediately
      setTrainingPrompts((prev) => {
        trainingPromptsRef.current = newPrompts; // Update ref immediately
        return newPrompts;
      });

    }

    let latestPrompts: TrainingPrompt[] | undefined = undefined;

    // If incorrect and not a retry vocable yet, add to retry set and re-add to prompts
    if (_grade < 1 && !isRetryVocable) {
      setRetryVocableIds((prev) => new Set(prev).add(vocableId));
      newPrompts = addVocableToPromptsAtRandom(newPrompts, currentPrompt, currentIndex);
    }
    
    // If retry vocable is still incorrect, re-add to prompts to ask again
    if (_grade < 1 && isRetryVocable) {
      newPrompts = addVocableToPromptsAtRandom(newPrompts, currentPrompt, currentIndex);
    }
    
    // For retry vocables, don't persist grade, just advance
    if (isRetryVocable) {
      advanceToNextVocable(newPrompts); // Pass the updated prompts
      return;
    }

    // TODO: Persist grade using `_grade` value for non-retry vocables.

    advanceToNextVocable(newPrompts); // Pass the updated prompts (undefined if not updated)
  };

  const handleConfigure = () => {
    setShowConfiguration(true);
  };

  const handleBackFromConfiguration = () => {
    setShowConfiguration(false);
    // Reload configuration after returning from config screen
    loadConfiguration();
  };

  const handleVocabulary = () => {
    setShowAddEditVocable(false);
    setShowViewVocable(false);
    setShowVocabulary(true);
    setVocableToView(null);
    setVocabularyRefreshToken((prev) => prev + 1);
  };

  const handleBackFromVocabulary = () => {
    setShowVocabulary(false);
    setShowViewVocable(false);
    setShowAddEditVocable(false);
  };

  const handleOpenAddVocable = () => {
    setVocableToEdit(null);
    setVocableToView(null);
    setReturnToViewAfterEdit(false);
    setShowVocabulary(false);
    setShowAddEditVocable(true);
  };

  const handleOpenViewVocable = (vocable: VocabularyListVocable) => {
    setVocableToView(vocable);
    setShowVocabulary(false);
    setShowViewVocable(true);
    setReturnToViewAfterEdit(false);
  };

  const handleEditVocableFromView = (vocable: VocabularyListVocable) => {
    setVocableToEdit(vocable);
    setShowViewVocable(false);
    setShowAddEditVocable(true);
    setReturnToViewAfterEdit(true);
  };

  const handleCancelAddEditVocable = () => {
    setShowAddEditVocable(false);
    setVocableToEdit(null);

    if (returnToViewAfterEdit && vocableToView) {
      setShowViewVocable(true);
      setShowVocabulary(false);
    } else {
      setShowViewVocable(false);
      setVocableToView(null);
      setShowVocabulary(true);
      setVocabularyRefreshToken((prev) => prev + 1);
    }

    setReturnToViewAfterEdit(false);
  };

  const handleSaveVocable = async (
    values: AddEditVocableFormValues,
    options?: { addAnother?: boolean }
  ): Promise<boolean> => {
    const isEditing = Boolean(vocableToEdit?.id);
    const endpoint = isEditing
      ? `/api/vocabulary/vocables/${vocableToEdit?.id}`
      : '/api/vocabulary/vocables';
    const method = isEditing ? 'PUT' : 'POST';

    const trimmedJapanese = values.japanese.trim();
    const trimmedKana = values.kana.trim();

    const payload: Record<string, unknown> = {
      japanese: trimmedJapanese,
      english: values.english,
      partOfSpeech: values.partOfSpeech,
    };

    if (trimmedKana) {
      payload.kana = trimmedKana;
    }

    if (isEditing && vocableToEdit?.id) {
      payload.id = vocableToEdit.id;
    }

    if (values.partOfSpeech === 'VERB') {
      if (values.verbType) {
        payload.verbType = values.verbType;
      }
      payload.transitive = Boolean(values.isTransitive);
    }

    try {
      const response = await authenticatedFetch(endpoint, {
        method,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          `Failed to ${isEditing ? 'update' : 'create'} vocable`,
          errorText || response.statusText
        );
        
        // Handle duplicate vocable error (409 Conflict)
        if (response.status === 409) {
          throw new Error('An identical vocable already exists.');
        }
        
        // Handle other errors
        throw new Error(`Failed to ${isEditing ? 'update' : 'create'} vocable. Please try again.`);
      }

      let responseData: any = null;
      try {
        responseData = await response.json();
      } catch (jsonError) {
        responseData = null;
      }

      if (options?.addAnother) {
        setVocabularyRefreshToken((prev) => prev + 1);
        setVocableToEdit(null);
        return true;
      }

      if (returnToViewAfterEdit && vocableToView) {
        const updatedVocable: VocabularyListVocable = {
          ...vocableToView,
          ...(responseData ?? {}),
          japanese: (responseData?.japanese ?? trimmedJapanese) as string,
          kana: (responseData?.kana ?? trimmedKana) as string,
          english: (responseData?.english ?? values.english) as string[],
          partOfSpeech: (responseData?.partOfSpeech ?? values.partOfSpeech) as string,
          verbType: (responseData?.verbType ?? values.verbType) as string | undefined,
          transitive:
            (typeof responseData?.transitive !== 'undefined'
              ? responseData.transitive
              : typeof responseData?.isTransitive !== 'undefined'
              ? responseData.isTransitive
              : values.isTransitive) ?? undefined,
        };

        setVocableToView(updatedVocable);
        setShowAddEditVocable(false);
        setShowViewVocable(true);
        setShowVocabulary(false);
        setVocableToEdit(null);
        setReturnToViewAfterEdit(false);
        setVocabularyRefreshToken((prev) => prev + 1);
      } else {
        setShowAddEditVocable(false);
        setShowViewVocable(false);
        setShowVocabulary(true);
        setVocableToEdit(null);
        setVocableToView(null);
        setReturnToViewAfterEdit(false);
        setVocabularyRefreshToken((prev) => prev + 1);
      }

      return true;
    } catch (error) {
      console.error(`Error while ${isEditing ? 'updating' : 'creating'} vocable`, error);
      // Re-throw the error so AddEditVocableScreen can display the specific error message
      throw error;
    }
  };

  const handlesubmitReviewResultsAndBackToStart = () => {
    submitReviewResults();
    handleBackToStart();
  };

  const handleBackToStart = () => {
    setState('start');
    setCurrentPrompt(null);
    setTrainingPrompts([]);
    setCurrentIndex(0);
    setTrainingError('');
    setUserInput('');
    setShowAnswer(false);
    setIsCorrect(false);
    setSessionComplete(false);
    setAnsweredCount(0);
    setCorrectCount(0);
    setGradeButtonsState('hidden');
    setTypoUsed(false);
    setWasAnswerEmpty(false);
    setFlashcardCorrectSelected(false);
    setFocusStatus(null);
    setRetryVocableIds(new Set());
    setReviewResults([]);
    reviewResultsRef.current = [];
    previousVocableIdRef.current = null;
  };

  const totalAnswered = answeredCount;
  const totalCorrect = correctCount;
  const accuracyPercentage = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;
  const isSummaryVisible = sessionComplete && showAnswer;
  const currentVocable = currentPrompt?.vocable ?? null;
  const currentPromptMode: PromptMode = currentPrompt?.promptMode ?? 'JAPANESE';
  const isRetryVocable = currentVocable ? retryVocableIds.has(currentVocable.id) : false;
  const isFlashcardMode = config?.outputMode === 'FLASHCARD';
  
  // Calculate training progress
  const initialPromptsCountRef = useRef<number>(0);
  const totalPrompts = initialPromptsCountRef.current;

  const currentProgress = totalPrompts > 0 
    ? Math.round(((currentCorrectIndex) / totalPrompts) * 100)
    : 0;

  const handleFocus = useCallback(async () => {
    if (!currentVocable || focusStatus === 'loading') return;

    setFocusStatus('loading');
    try {
      const response = await authenticatedFetch(`/api/vocabulary/vocables/${currentVocable.id}/focus`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to focus vocable');
      }

      const updatedVocable: TrainingVocable = await response.json();
      
      // Update the vocable in current prompt
      if (currentPrompt) {
        setCurrentPrompt({
          ...currentPrompt,
          vocable: updatedVocable,
        });
      }

      setFocusStatus('focused');
    } catch (error) {
      console.error('Error focusing vocable:', error);

      setFocusStatus('base-focus');
    }
  }, [currentVocable, currentPrompt, focusStatus]);

  const handleUnfocus = useCallback(async () => {
    if (!currentVocable || focusStatus === 'loading') return;

    setFocusStatus('loading');
    try {
      const response = await authenticatedFetch(`/api/vocabulary/vocables/${currentVocable.id}/unfocus`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to unfocus vocable');
      }

      const updatedVocable: TrainingVocable = await response.json();
      
      // Update the vocable in current prompt
      if (currentPrompt) {
        setCurrentPrompt({
          ...currentPrompt,
          vocable: updatedVocable,
        });
      }

      setFocusStatus('base-focus');
    } catch (error) {
      console.error('Error unfocusing vocable:', error);
      // Reset to previous state on error
      const wasFocused = (currentVocable?.focusIndex ?? 0) > 0;
      setFocusStatus(wasFocused ? 'focused' : 'base-focus');
    }
  }, [currentVocable, currentPrompt, focusStatus]);

  const handleFocusToggle = useCallback(() => {
    if (focusStatus === 'focused') {
      handleUnfocus();
    } else if (focusStatus === 'base-focus') {
      handleFocus();
    }
  }, [focusStatus, handleFocus, handleUnfocus]);

  // Update focus status when current vocable changes (only when ID changes)
  useEffect(() => {
    if (currentVocable) {
      const currentId = currentVocable.id;
      // Only reset to base-focus if this is a different vocable (ID changed)
      if (previousVocableIdRef.current !== currentId) {
        setFocusStatus('base-focus');
        previousVocableIdRef.current = currentId;
      }
    } else {
      setFocusStatus(null);
      previousVocableIdRef.current = null;
    }
  }, [currentVocable]);

  // Keep trainingPromptsRef in sync with trainingPrompts state
  useEffect(() => {
    trainingPromptsRef.current = trainingPrompts;
  }, [trainingPrompts]);

  useEffect(() => {
    reviewResultsRef.current = reviewResults;
  }, [reviewResults]);
  const englishPrompt = currentPrompt?.englishPrompt;
  const isEnglishPrompt = currentPromptMode === 'ENGLISH';
  const nonDesiredVocables = currentVocable?.nonDesiredVocables?.trim();
  const promptDisplayText = isEnglishPrompt ? englishPrompt ?? '' : currentVocable?.japanese ?? '';
  const shouldUseCompactVocableSize =
    promptDisplayText.length > (isEnglishPrompt ? 16 : 13);
  const japaneseAnswerParts = useMemo(() => {
    if (!currentVocable) {
      return [];
    }

    return [currentVocable.japanese, currentVocable.kana].filter(
      (value): value is string => Boolean(value && value.trim().length > 0)
    );
  }, [currentVocable]);
  const englishAnswerParts = useMemo(() => {
    if (!currentVocable) {
      return [];
    }
    return (currentVocable.english ?? []).filter((value) => Boolean(value && value.trim().length > 0));
  }, [currentVocable]);
  const fullJapaneseAnswerText = japaneseAnswerParts.join(', ');
  const fullEnglishAnswerText = englishAnswerParts.join(', ');
  const summaryText = `You answered ${totalCorrect} of ${totalAnswered} correctly (${accuracyPercentage}%).`;
  const correctAnswerPlaceholder =
    isEnglishPrompt && nonDesiredVocables
      ? `Answer is not ${nonDesiredVocables}`
      : 'Correct answer will appear here';
  const shouldTruncateAnswers =
    Boolean(answerDisplayWidth && answerDisplayWidth > 0) && showAnswer && !isSummaryVisible && Boolean(currentVocable);
  const truncatedJapaneseAnswerText = shouldTruncateAnswers
    ? buildAnswerWithinWidth(japaneseAnswerParts, answerDisplayWidth, answerDisplayFont)
    : fullJapaneseAnswerText;
  const truncatedEnglishAnswerText = shouldTruncateAnswers
    ? buildAnswerWithinWidth(englishAnswerParts, answerDisplayWidth, answerDisplayFont)
    : fullEnglishAnswerText;
  const fullAnswerText = isSummaryVisible
    ? summaryText
    : showAnswer && currentVocable
      ? isEnglishPrompt
        ? fullJapaneseAnswerText || correctAnswerPlaceholder
        : fullEnglishAnswerText || correctAnswerPlaceholder
      : correctAnswerPlaceholder;

  const showVerbModeText = config?.languageMode === 'VERBS';
  const showSpecificVerbModeText = showVerbModeText && config?.includedConjugations && config.includedConjugations.length <= 3;

  const verbModeText = showSpecificVerbModeText ? `Allowed conjugations: ${config?.includedConjugations?.map(conj => {
    const form = VERB_FORMS.find(f => f.value === conj);
    return form ? form.englishName : conj;
  }).join(', ')}` : `${config?.includedConjugations?.length} conjugations are allowed`;
  
  const displayedCorrectAnswerText = isSummaryVisible
    ? summaryText
    : showVerbModeText ? verbModeText : showAnswer && currentVocable
      ? isEnglishPrompt
        ? truncatedJapaneseAnswerText || fullJapaneseAnswerText || correctAnswerPlaceholder
        : truncatedEnglishAnswerText || fullEnglishAnswerText || correctAnswerPlaceholder
      : correctAnswerPlaceholder;

  useLayoutEffect(() => {
    const updateMeasurements = () => {
      const element = correctAnswerRef.current;

      if (!element) {
        setAnswerDisplayWidth(null);
        return;
      }

      setAnswerDisplayWidth(element.clientWidth);
      
      // In FLASHCARD mode, use answer-status font style
      let computed: CSSStyleDeclaration;
      if (config?.outputMode === 'FLASHCARD') {
        const answerStatusElement = element.parentElement?.querySelector('.answer-status');
        computed = answerStatusElement 
          ? window.getComputedStyle(answerStatusElement)
          : window.getComputedStyle(element);
      } else {
        computed = window.getComputedStyle(element);
      }
      
      const fontValue =
        computed.font && computed.font !== 'normal normal normal 16px / normal serif'
          ? computed.font
          : `${computed.fontWeight} ${computed.fontSize} ${computed.fontFamily}`;
      setAnswerDisplayFont(fontValue);
    };

    updateMeasurements();
    window.addEventListener('resize', updateMeasurements);
    return () => {
      window.removeEventListener('resize', updateMeasurements);
    };
  }, [showAnswer, isSummaryVisible, currentVocable, config?.outputMode]);

  // Show login screen if not authenticated
  if (appState === 'login') {
    return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
  }

  // Show imprint screen
  if (showImprintScreen) {
    return <ImprintScreen onBack={() => setShowImprintScreen(false)} />;
  }

  if (showAddEditVocable) {
    return (
      <AddEditVocableScreen
        onBack={handleCancelAddEditVocable}
        onSave={handleSaveVocable}
        initialVocable={vocableToEdit ?? undefined}
      />
    );
  }

  if (showViewVocable && vocableToView) {
    const handleDeleteVocable = async () => {
      if (!vocableToView?.id) {
        return;
      }

      try {
        const response = await authenticatedFetch(
          `/api/vocabulary/${vocableToView.id}`,
          { method: 'DELETE' }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Failed to delete vocable', errorText || response.statusText);
          alert('Failed to delete vocable. Please try again.');
          return;
        }

        setShowViewVocable(false);
        setVocableToView(null);
        setShowVocabulary(true);
        setVocabularyRefreshToken((prev) => prev + 1);
      } catch (error) {
        console.error('Error while deleting vocable', error);
        alert('Failed to delete vocable. Please try again.');
      }
    };

    return (
      <ViewVocableScreen
        vocable={vocableToView}
        onBack={() => {
          setShowViewVocable(false);
          setVocableToView(null);
          setShowVocabulary(true);
          setVocabularyRefreshToken((prev) => prev + 1);
        }}
        onEdit={handleEditVocableFromView}
        onDelete={handleDeleteVocable}
      />
    );
  }

  // Show configuration screen
  if (showConfiguration) {
    return <ConfigurationScreen onBack={handleBackFromConfiguration} onLogout={handleLogout} />;
  }

  // Show vocabulary screen
  if (showVocabulary) {
    return (
      <VocabularyScreen
        onBack={handleBackFromVocabulary}
        onAddVocable={handleOpenAddVocable}
        onViewVocable={handleOpenViewVocable}
        refreshToken={vocabularyRefreshToken}
      />
    );
  }

  return (
    <div className="app">
      <main className="main-content">
        {state === 'start' && (
          <div className="start-screen">
            <h1 className="app-title">Vocabulary Trainer</h1>
            <p className="app-subtitle">{getConfigSummary()}</p>
            {trainingError && <div className="error-message">{trainingError}</div>}
            <div className="button-row">
              <button className="btn btn-secondary" onClick={handleConfigure}>
                Configure
              </button>
              <button className="btn btn-secondary" onClick={handleVocabulary}>
                Vocabulary
              </button>
              <button
                className="btn btn-primary"
                onClick={handleStart}
                disabled={isTrainingLoading}
              >
                {isTrainingLoading ? 'Starting...' : 'Start'}
              </button>
            </div>
            <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid var(--border-color)' }} />
            <div style={{ marginTop: '-5px', marginBottom: '-18px', display: 'flex', justifyContent: 'center' }}>
              <button
                type="button"
                className="imprint-button"
                onClick={() => setShowImprintScreen(true)}
              >
                About & Impressum
              </button>
            </div>
          </div>
        )}

        {state === 'training' && (
          <div className="training-screen">
            <div className="header-bar">
              <button className="back-button" onClick={handleBackToStart}>
                ← Back
              </button>
              {totalPrompts > 0 && (
                <div className="progress-bar-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-bar-fill" 
                      style={{ width: `${currentProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
              {currentVocable && (
                <button
                  className={`focus-button ${
                    focusStatus === 'focused' 
                      ? 'focused' 
                      : 'base-focus'
                  }`}
                  onClick={handleFocusToggle}
                  disabled={focusStatus === 'loading' || focusStatus === null}
                  title={
                    focusStatus === 'focused' 
                      ? 'Click to unfocus' 
                      : focusStatus === 'base-focus' 
                        ? 'Click to focus' 
                        : ''
                  }
                >
                  {focusStatus === 'loading' 
                    ? 'Focused' 
                    : focusStatus === 'focused' 
                      ? 'Focused' 
                      : 'Focus' 
                  }
                </button>
              )}
            </div>

            {isTrainingLoading && (
              <div className="loading-state">Loading training session...</div>
            )}

            {!isTrainingLoading && currentVocable && (
              <>
                <div className="vocable-display">
                  <div className="prompt-wrapper">
                    <>
                      <div
                        className={`vocable-japanese ${
                          shouldUseCompactVocableSize ? 'vocable-japanese--compact' : ''
                        }`}
                      >
                        {promptDisplayText}
                      </div>
                      <div className="vocable-kana">
                        {(!showAnswer && !sessionComplete) || isEnglishPrompt
                          ? '\u3000'
                          : currentVocable.kana !== currentVocable.japanese
                            ? currentVocable.kana
                            : '\u3000'}
                      </div>
                    </>
                  </div>
                </div>

                <div
                  className={`answer-feedback ${
                    (isSummaryVisible || isFlashcardMode)
                      ? 'summary neutral'
                      : showAnswer
                        ? (isCorrect ? 'correct' : 'incorrect')
                        : 'neutral'
                  }`}
                >
                  <span className="answer-status">
                    {isSummaryVisible
                      ? 'Session Summary'
                      : showAnswer
                        ? (isFlashcardMode ? displayedCorrectAnswerText :isCorrect ? '✓ Correct!' : '✗ Incorrect')
                        : '⏳'}
                  </span>
                  <div className="correct-answer" ref={correctAnswerRef} title={fullAnswerText}>
                    {(isFlashcardMode && showAnswer && !isSummaryVisible) ? 'Was your answer correct?' :displayedCorrectAnswerText}
                  </div>
                </div>

                

                {!isFlashcardMode && (
                  <div className="input-area">
                    <input
                      type="text"
                      className="vocable-input"
                      placeholder={isEnglishPrompt ? 'Enter Japanese translation...' : 'Enter English translation...'}
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onCompositionStart={() => setIsComposing(true)}
                      onCompositionEnd={() => setIsComposing(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !showAnswer && !isComposing) {
                          handleCheckAnswer();
                        }
                      }}
                      disabled={showAnswer}
                      autoFocus
                    />
                  </div>
                )}

                <div className="button-row">
                  {sessionComplete ? (
                    <button className="btn btn-primary" onClick={handlesubmitReviewResultsAndBackToStart}>
                      Save and back
                    </button>
                  ) : !showAnswer ? (
                    <button
                      className="btn btn-primary"
                      onClick={handleCheckAnswer}
                    >
                      Check
                    </button>
                  ) : null}
                </div>

                {showAnswer && !sessionComplete && (
                  <div className="grade-buttons">
                    {isFlashcardMode ? (
                      flashcardCorrectSelected ? (
                        <>
                        {isRetryVocable ? (
                          <>
                            <button className="btn btn-primary" onClick={() => handleGrade(3)}>
                              Continue
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="btn btn-primary" onClick={() => handleGrade(1)}>
                              Hard
                            </button>
                            <button className="btn btn-primary" onClick={() => handleGrade(3)}>
                              Easy
                            </button>
                          </>
                        )}
                        </>
                      ) : (
                        <>
                          <button className="btn btn-primary" onClick={() => handleGrade(0)}>
                            Wrong
                          </button>
                          <button className="btn btn-primary" onClick={() => setFlashcardCorrectSelected(true)}>
                            Correct
                          </button>
                        </>
                      )
                    ) : (
                      <>
                        {wasAnswerEmpty ? (
                          <button className="btn btn-primary" onClick={() => handleGrade(0)}>
                            Forgot
                          </button>
                        ) : (
                          <>
                            {gradeButtonsState === 'correct' && (
                              <>
                                {isRetryVocable ? (
                                  <button className="btn btn-primary" onClick={() => handleGrade(3)}>
                                    Continue
                                  </button>
                                ) : (
                                  <>
                                    <button className="btn btn-primary" onClick={() => handleGrade(1)}>
                                      Hard
                                    </button>
                                    <button className="btn btn-primary" onClick={() => handleGrade(3)}>
                                      Easy
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                            {gradeButtonsState === 'incorrect' && (
                              <>
                                <button className="btn btn-primary" onClick={() => handleGrade(0)}>
                                  Forgot
                                </button>
                                <button className="btn btn-primary" onClick={handleTypo}>
                                  Typo
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}

            {!isTrainingLoading && !currentVocable && (
              <div className="empty-state">
                No training vocables available. Return to start and try again later.
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
